<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { AvatarFallback, AvatarImage, AvatarRoot } from 'reka-ui';
import { ChevronDown, Menu, X, Check, Loader2 } from '@lucide/vue';
import { gotoLogin, clearToken } from '@/utils/auth';
import { useUserStore, useAppStore } from '@/stores';
import { useJoinCommunity } from '@/hooks/useJoinCommunity';
import logoFallback from '@/assets/logo.svg';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const route = useRoute();
const userStore = useUserStore();
const appStore = useAppStore();
const { isJoined, joining, handleJoin } = useJoinCommunity();
const mobileMenuOpen = ref(false);

const { siteLogoUrl, siteName, siteTitle } = storeToRefs(appStore);
const logoSrc = computed(() => siteLogoUrl.value || logoFallback);
const logoAlt = computed(() => siteName.value || '');
// Logo 右侧站点名：优先接口 title（如「FOSS LEGAL」），缺失时回退 name
const siteBrand = computed(() => siteTitle.value || siteName.value || '');

const isLoggedIn = computed(() => Boolean(userStore.user.id));
const displayName = computed(() => userStore.user.nickname || userStore.user.username || '用户');
const userInitial = computed(() => displayName.value.charAt(0).toUpperCase());

// 下拉进出场动效
const menuAnim = 'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2';

// 专题专区下拉
const zoneLinks = [
  { label: '人工智能专区', to: '/zones/ai' },
  { label: '开源法律话题专区', to: '/zones/legal-topics' },
  { label: '沙龙活动专区', to: '/zones/salons' },
  { label: '开放原子系列许可证专区', to: '/zones/licenses' },
  { label: '译文专区', to: '/zones/translations' },
  { label: '提问讨论版', to: '/questions' },
];

const navLinks = [
  { label: '首页', path: '/' },
  { label: '司法案例', path: '/cases' },
  { label: '贡献者库', path: '/experts' },
  { label: '社区资讯', path: '/news' },
  { label: '开源法律课程', path: '/courses' },
];

const firstNavLinks = navLinks.slice(0, 1);
const restNavLinks = navLinks.slice(1);

function isActive(path: string) {
  if (path === '/') {
    return route.path === '/';
  }

  return route.path.startsWith(path);
}

function isZoneActive() {
  return route.path.startsWith('/zones');
}

function closeMobileMenu() {
  mobileMenuOpen.value = false;
}

function toggleMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

const handleLogin = () => {
  gotoLogin();
};

function handleLogout() {
  clearToken();

  location.reload();
}
</script>

<template>
  <header
    class="sticky top-0 z-1000 bg-white/96 backdrop-blur-[24px] border-b border-border-subtle"
    :class="mobileMenuOpen ? 'max-lg:bg-surface-card max-lg:backdrop-blur-none' : ''"
  >
    <div class="container-main flex items-center gap-7 h-(--nav-height)">
      <!-- 品牌：Logo + 站点名 -->
      <RouterLink to="/" class="inline-flex items-center gap-3 no-underline shrink-0">
        <img
          :src="logoSrc"
          :alt="logoAlt"
          referrerpolicy="no-referrer"
          class="h-9.5 align-middle"
        >
        <!-- 站点配置的 logo 是含站名的完整组合图，仅在未配置 logo、退回本地图标时才补站名文字 -->
        <span
          v-if="siteBrand && !siteLogoUrl"
          class="text-[17px] font-bold text-text-primary tracking-[-0.3px] whitespace-nowrap"
        >
          {{ siteBrand }}
        </span>
      </RouterLink>

      <!-- 桌面端导航 -->
      <nav aria-label="主导航" class="hidden lg:flex items-center gap-1.5 flex-1 min-w-0 justify-end">
        <RouterLink
          v-for="link in firstNavLinks"
          :key="link.path"
          :to="link.path"
          class="px-3 py-1.5 rounded-md text-[13px] font-medium transition-all duration-200 cursor-pointer no-underline"
          :class="isActive(link.path)
            ? 'text-(--color-primary) bg-(--color-primary-light) font-semibold'
            : 'text-text-secondary hover:text-(--color-primary) hover:bg-(--color-primary-light)'"
          @click="closeMobileMenu()"
        >
          {{ link.label }}
        </RouterLink>

        <!-- 专题专区下拉 -->
        <DropdownMenu :modal="false">
          <DropdownMenuTrigger as-child>
            <button
              type="button"
              class="group flex items-center gap-1 px-3 py-1.5 rounded-md text-[13px] font-medium transition-all duration-200 cursor-pointer border-none"
              :class="isZoneActive()
                ? 'text-(--color-primary) bg-(--color-primary-light) font-semibold'
                : 'text-text-secondary bg-transparent hover:text-(--color-primary) hover:bg-(--color-primary-light)'"
            >
              专题专区
              <ChevronDown :size="13" class="transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent class="z-[1100] min-w-[220px]" :class="menuAnim">
            <DropdownMenuItem
              v-for="zone in zoneLinks"
              :key="zone.to"
              as-child
              class="text-[13px] text-text-secondary cursor-pointer"
            >
              <RouterLink :to="zone.to" class="no-underline">
                {{ zone.label }}
              </RouterLink>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <template v-for="link in restNavLinks" :key="link.path">
          <RouterLink
            :to="link.path"
            class="px-3 py-1.5 rounded-md text-[13px] font-medium transition-all duration-200 cursor-pointer no-underline"
            :class="isActive(link.path)
              ? 'text-(--color-primary) bg-(--color-primary-light) font-semibold'
              : 'text-text-secondary hover:text-(--color-primary) hover:bg-(--color-primary-light)'"
            @click="closeMobileMenu()"
          >
            {{ link.label }}
          </RouterLink>
        </template>

        <div class="flex items-center gap-2.5 ml-3 pl-3.5 border-l border-border-default">
          <button
            type="button"
            :disabled="joining || isJoined"
            class="inline-flex items-center gap-1.5 px-5 py-[7px] rounded-md text-[13px] font-semibold bg-(--color-primary) text-white border-none cursor-pointer transition-all duration-200 whitespace-nowrap hover:bg-(--color-primary-hover) hover:-translate-y-px hover:shadow-brand-sm disabled:opacity-60 disabled:cursor-default disabled:hover:translate-y-0 disabled:hover:shadow-none"
            @click="handleJoin"
          >
            <Loader2 v-if="joining" :size="14" class="animate-spin" />
            <Check v-else-if="isJoined" :size="14" />
            {{ isJoined ? '已加入社区' : '加入社区' }}
          </button>

          <!-- 未登录 -->
          <button
            v-if="!isLoggedIn"
            type="button"
            class="px-[18px] py-1.5 rounded-md text-[13px] font-semibold bg-transparent text-(--color-primary) border border-(--color-primary) cursor-pointer transition-all duration-200 whitespace-nowrap hover:bg-(--color-primary-light)"
            @click="handleLogin"
          >
            登录
          </button>

          <!-- 已登录 -->
          <DropdownMenu v-else :modal="false">
            <DropdownMenuTrigger as-child>
              <button
                type="button"
                class="bg-linear-135/srgb from-(--color-primary) to-(--avatar-gradient-to) inline-flex items-center justify-center w-[34px] h-[34px] rounded-full border-none cursor-pointer outline-none transition-all duration-200 hover:-translate-y-px hover:shadow-brand"
              >
                <AvatarRoot class="w-full h-full rounded-full overflow-hidden">
                  <AvatarImage
                    v-if="userStore.user.photo"
                    :src="userStore.user.photo"
                    alt="用户头像"
                    class="w-full h-full rounded-full object-cover"
                  />
                  <AvatarFallback
                    :delay-ms="600"
                    class="w-full h-full flex items-center justify-center text-white text-sm font-bold rounded-full"
                  >
                    {{ userInitial }}
                  </AvatarFallback>
                </AvatarRoot>
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent class="z-[1100]" :class="menuAnim">
              <!-- 用户信息 -->
              <div class="px-3 py-2.5">
                <p class="text-sm font-semibold text-text-primary leading-tight">{{ displayName }}</p>
                <p class="text-xs text-text-tertiary leading-tight mt-0.5">@{{ userStore.user.username }}</p>
              </div>

              <DropdownMenuSeparator />

              <DropdownMenuItem class="text-(--color-destructive) cursor-pointer" @click="handleLogout">
                退出登录
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      <!-- 移动端汉堡按钮 -->
      <button
        type="button"
        class="lg:hidden ml-auto flex items-center justify-center w-10 h-10 rounded-md border border-border-default bg-transparent cursor-pointer"
        aria-label="Toggle menu"
        @click="toggleMenu"
      >
        <X v-if="mobileMenuOpen" :size="20" class="text-text-secondary" />
        <Menu v-else :size="20" class="text-text-secondary" />
      </button>
    </div>

    <!-- 移动端菜单面板 -->
    <div
      class="lg:hidden absolute top-full left-0 right-0 bg-surface-card shadow-[var(--shadow-lg)] overflow-hidden transition-all duration-300 ease-out"
      :class="mobileMenuOpen ? 'max-h-[560px] opacity-100' : 'max-h-0 opacity-0'"
    >
      <div class="container-main py-4 flex flex-col">
        <!-- 导航链接 -->
        <nav aria-label="移动端导航" class="flex flex-col gap-1">
          <RouterLink
            v-for="link in firstNavLinks"
            :key="link.path"
            :to="link.path"
            class="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-150 cursor-pointer border-none no-underline"
            :class="isActive(link.path)
              ? 'bg-(--color-primary-light) text-(--color-primary) font-semibold'
              : 'bg-transparent text-text-secondary hover:bg-(--color-primary-light)'"
            @click="closeMobileMenu()"
          >
            {{ link.label }}
          </RouterLink>

          <details class="group">
            <summary class="list-none cursor-pointer flex items-center justify-between px-4 py-3 rounded-md text-sm font-medium text-text-secondary hover:bg-(--color-primary-light)">
              专题专区
              <ChevronDown :size="14" class="transition-transform duration-200 group-open:rotate-180" />
            </summary>
            <div class="flex flex-col gap-1 pl-3">
              <RouterLink
                v-for="zone in zoneLinks"
                :key="zone.to"
                :to="zone.to"
                class="px-4 py-2.5 rounded-md text-[13px] text-text-secondary no-underline hover:bg-(--color-primary-light) hover:text-(--color-primary)"
                @click="closeMobileMenu()"
              >
                {{ zone.label }}
              </RouterLink>
            </div>
          </details>

          <RouterLink
            v-for="link in restNavLinks"
            :key="link.path"
            :to="link.path"
            class="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-150 cursor-pointer border-none no-underline"
            :class="isActive(link.path)
              ? 'bg-(--color-primary-light) text-(--color-primary) font-semibold'
              : 'bg-transparent text-text-secondary hover:bg-(--color-primary-light)'"
            @click="closeMobileMenu()"
          >
            {{ link.label }}
          </RouterLink>
        </nav>

        <!-- 分隔线 -->
        <div class="border-t border-border-subtle my-3"></div>

        <!-- 操作按钮 -->
        <div class="flex flex-col gap-2">
          <button
            type="button"
            :disabled="joining || isJoined"
            class="w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-md text-sm font-semibold border-none text-white bg-(--color-primary) cursor-pointer transition-all duration-200 hover:bg-(--color-primary-hover) disabled:opacity-60 disabled:cursor-default"
            @click="handleJoin"
          >
            <Loader2 v-if="joining" :size="16" class="animate-spin" />
            <Check v-else-if="isJoined" :size="16" />
            {{ isJoined ? '已加入社区' : '加入社区' }}
          </button>

          <template v-if="!isLoggedIn">
            <button
              type="button"
              class="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold cursor-pointer bg-transparent text-(--color-primary) border border-(--color-primary) transition-all duration-200 hover:bg-(--color-primary-light)"
              @click="handleLogin"
            >
              登录
            </button>
          </template>

          <template v-else>
            <!-- 已登录用户信息 -->
            <div class="flex items-center gap-3 px-4 py-3">
              <AvatarRoot class="w-10 h-10 rounded-full border-2 border-border-default shrink-0">
                <AvatarImage
                  v-if="userStore.user.photo"
                  :src="userStore.user.photo"
                  alt="用户头像"
                  class="w-full h-full rounded-full object-cover"
                />
                <AvatarFallback
                  :delay-ms="600"
                  class="w-full h-full flex items-center justify-center bg-(--color-primary) text-white text-sm font-semibold rounded-full"
                >
                  {{ userInitial }}
                </AvatarFallback>
              </AvatarRoot>
              <div class="flex flex-col min-w-0">
                <span class="text-sm font-semibold text-text-primary leading-tight">{{ displayName }}</span>
                <span class="text-xs text-text-tertiary leading-tight mt-0.5">@{{ userStore.user.username }}</span>
              </div>
            </div>

            <button
              type="button"
              class="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer border-none bg-(--zone-activity-light) text-(--color-destructive) hover:bg-(--color-destructive-bg)"
              @click="handleLogout"
            >
              退出登录
            </button>
          </template>
        </div>
      </div>
    </div>
  </header>
</template>
